import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ConfirmDeleteModal } from "@components/UI";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, uppercase, formatDateForRequest, translateDepartment } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { getIndexDBData, STORES, updateDataInIndexedDb } from "@utils/indexedDb";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

function UserList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasEditPermission, hasDeletePermission } = useRoutePermissions();
  const { colors } = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [userList, setUserList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.USER_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
    const [isUserListLoading, setIsUserListLoading] = useState(false);
  const isInitialMount = useRef(true);
  const prevSearchFilters = useRef({});
  const prevCurrentPage = useRef(1);

  const { assignableRolesList, departmentList, fetchSearchInfo } = useSearchInfo();
  const [creatorOptions, setCreatorOptions] = useState([]);

  // Search fields for advanced search
  const searchFields = [
    {
      name: "userId",
      label: t("system.user-id"),
      placeholder: t("system.user.user-id-placeholder"),
    },
    {
      name: "userName",
      label: t("system.user-name"),
      placeholder: t("system.user.user-name-placeholder"),
    },
    // {
    //   name: "deptCd",
    //   label: t("system.user.department"),
    //   type: "select",
    //   keepRawLabel: true,
    //   options: departmentList.map((item) => ({
    //     label: translateDepartment(t, item.value),
    //     value: item.value,
    //   })),
    //   placeholder: t("system.user.department-placeholder"),
    // },
    {
      name: "email",
      label: t("system.user.email"),
      placeholder: t("system.user.email-placeholder"),
    },
    {
      name: "roleCd",
      label: t("system.user.role"),
      placeholder: t("system.user.role-placeholder"),
      type: "select",
      keepRawLabel: true,
      options: assignableRolesList,
    },
    {
      name: "createrId",
      label: t("system.creator"),
      type: "select",
      keepRawLabel: true,
      options: creatorOptions,
      placeholder: t("system.creator-placeholder"),
      displayFormatLabelWithID: true,
    },
    {
      name: "createdAtFrom",
      label: t("system.creation-date-from"),
      placeholder: t("system.creation-date-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: t("system.creation-date-to"),
      placeholder: t("system.creation-date-to"),
      type: "date",
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);


  const columns = [
    "userId",
    "userName",
    // "deptCdName",
    "email",
    "roleCdName",
    "enableFlag",
    "createrName",
    "createdAt",
  ];

  const columnConfig = {
    userId: {
      style: { width: "12%" },
      headerRender: () => t("system.user.user-id"),
    },
    userName: {
      style: { width: "12%" },
      headerRender: () => t("system.user.user-name"),
    },
    // deptCdName: {
    //   style: { width: "12%" },
    //   headerRender: () => t("system.user.department"),
    //   customRender: (value) => translateDepartment(t, value),
    // },
    email: {
      style: { width: "15%" },
      headerRender: () => t("system.user.email"),
    },
    roleCdName: {
      style: { width: "10%" },
      headerRender: () => t("system.user.role"),
    },
    enableFlag: {
      style: { width: "10%" },
      headerRender: () => t("system.user.enable"),
      customRender: (value) => {
        const isEnabled = value === "Y";
        return (
          <span className={`inline-flex items-center text-sm ${isEnabled ? colors.textStatusHealthy : colors.textNeutralMuted}`}>
            <span className={`mr-2 h-2 w-2 rounded-full ${isEnabled ? colors.bgStatusHealthy : colors.bgStatusMuted}`}></span>
            {isEnabled ? t("system.user.enable") : t("system.user.disable")}
          </span>
        );
      },
    },
    createrName: {
      style: { width: "10%" },
      headerRender: () => t("system.creater-name"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      style: { width: "12%" },
      headerRender: () => t("bulletin.creation-date"),
      customRender: (value) => formatDateTime(value),
    }
  };

  const getListData = async (bodyRequest = {}) => {
    setIsUserListLoading(true);
    try {
      const { pageSize = itemsPerPage, startRow = 0, ...filters } = bodyRequest;
      const response = await getIndexDBData(STORES.USERS, {
        pageSize,
        startRow,
        filters,
        sortKey: "createdAt",
      });

      if (response && typeof response !== "string") {
        setUserList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching users from IndexedDB:", error);
      ToastConstant(TOAST.error, "Failed to fetch user list");
    } finally {
      setIsUserListLoading(false);
    }
  };

  const deleteUser = useMutationData(
    (userId) => `${queryKeys.deleteUser}/${userId}`,
    {
      method: "DELETE",
    },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.USERS, response, response.userId);
        ToastConstant(TOAST.success, t("system.user.delete-success"));
        const deletedCount = Array.isArray(selectedRows) ? selectedRows.length : 1;
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount,
          fetchData,
        });
        setSelectedRows([]);
        handleModalDeleteClose();
      },
      onError: (error) => {
        console.error("Error during delete user:", error);
        const { status, data } = error.response || {};
        const errorMsg = status === 400 && data ? data : "";
        let subErrMsg = "";
        switch (errorMsg) {
          case "This user is currently assigned as a Product Manager for one or more Products. Please reassign those Products before disabling the user.":
            subErrMsg = t("system.user.disable-error-product-manager-assignment");
            break;
          case "This user is currently assigned as a Project Manager for one or more Projects. Please reassign those Projects before disabling the user.":
            subErrMsg = t("system.user.disable-error-project-manager-assignment");
            break;
          default:
            subErrMsg = "";
        }
        ToastConstant(TOAST.error, t("system.user.delete-fail"), subErrMsg);
      },
    },
  );

  // Effects
  useEffect(() => {
    getSearchCreators.mutateAsync();
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  // Call API when itemsPerPage changes
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [itemsPerPage]);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  useEffect(() => {
    if (!isInitialMount.current) {
      const currentFilters = JSON.stringify(searchFilters);
      const prevFilters = JSON.stringify(prevSearchFilters.current);

      if (currentFilters !== prevFilters) {
        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          fetchData();
        }
        prevSearchFilters.current = searchFilters;
      }
    } else {
      isInitialMount.current = false;
      prevSearchFilters.current = searchFilters;
      prevCurrentPage.current = currentPage;
    }
  }, [searchFilters]);

  // Functions
  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      let value = effectiveCriteria[key];
      if (typeof value === "string") {
        value = value.trim();
      }
      if (value !== "" && value !== undefined) {
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});
    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    getListData(bodyRequest);
  };

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);
  const handleDelete = (row) => {
    setSelectedRows([row.userId]);
    setShowDeleteModal(true);
  };
  const handleModalDeleteClose = () => setShowDeleteModal(false);
  const handleCancelDelete = () => setShowDeleteModal(false);

  const handleCheckboxChange = (newSelectedRows) => setSelectedRows([...newSelectedRows]);
  const onRowClick = (rowData) => navigate(`./${rowData.userId}`);

  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteUser.mutateAsync(selectedRows);
      setSelectedRows([]);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting users:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getSearchCreators = useMutationData(
    queryKeys.getSearchCreatorList,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (Array.isArray(response)) {
          const mappedOptions = response.map(item => ({
            ...item,
            value: item.createrId,
            label: item.createrName
          }));
          setCreatorOptions(mappedOptions);
        }
      },
      onError: (error) => {
        console.error("Error fetching search info data:", error);
      },
    }
  );

  const handleAddUser = () => {
    navigate("/user/new");
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="user-list-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className={`text-[28px] font-bold ${colors.textPrimary} uppercase`}>{t("system.title")}</h1>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`}>
        <TableHeaderControls
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onAdd={handleAddUser}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("system.user.add-user")}
          isDisplayAddButton={hasEditPermission}
          isDisplayDeleteButton={false}
          searchInputTestId="user-list-search-input"
          addButtonTestId="user-list-add-button"
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="user-list-advanced-search">
          <AdvancedSearch
            title={t("project.advancedSearch")}
            searchFields={searchFields}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      <div className="flex-grow min-h-0" data-testid="user-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={userList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isUserListLoading}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          hasDeleteColumn={hasDeletePermission}
          onDeleteRow={handleDelete}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={false}
          hasRadioButton={false}
          rowKey="userId"
          shouldShowGoToPage={true}
          isDeleteDisabled={(row) => row.enableFlag === "N"}
          showItemsPerPageSelector={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="user-list-confirm-delete-modal"
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={uppercase(t("system.user.delete-user"))}
        description={t("system.user.confirm-delete-user-description", { count: selectedRows.length })}
        loading={deleteLoading}
      />
    </div>
  );
}

export default UserList;